import React, { useEffect, useMemo, useState } from 'react';
import Card from '../../components/ui/Card';
import Btn from '../../components/ui/Btn';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import { Input, Select } from '../../components/ui/Input';
import { folhaPagamentoApi, ajustesFinanceirosApi } from '../../api/rh';
import { diasUteisNoMes } from './beneficiosCalc';
import { COLORS } from '../../theme';

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
].map((m, i) => ({ value: String(i + 1), label: m }));

const ANO_ATUAL = new Date().getFullYear();
const ANOS = Array.from({ length: 5 }, (_, i) => ANO_ATUAL - 2 + i).map(a => ({ value: String(a), label: String(a) }));

function formatarReais(v) {
  return (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function elegivelSalarioFamilia(dataNascimento) {
  if (!dataNascimento) return false;
  const idade = (Date.now() - new Date(dataNascimento).getTime()) / (365.25 * 24 * 60 * 60 * 1000);
  return idade < 14;
}

function calcularFuncionario(f, beneficio, pontoMes, ajustesFunc, config, mes, ano, qtdFilhosElegiveis, comissao) {
  const salario = Number(f.salario) || 0;
  const faltas = Number(pontoMes?.faltas) || 0;
  const horasExtras = Number(pontoMes?.horas_extras) || 0;

  const diasUteis = diasUteisNoMes(Number(mes), Number(ano), f.dias_semana);
  const diasEfetivos = Math.max(0, diasUteis - faltas);

  const descontoFalta = (salario / 30) * faltas;
  const valorExtra = (salario / 220) * 1.5 * horasExtras;
  const adicionalNoturno = /noturno/i.test(f.turno || '') ? salario * (config.adicional_noturno_pct / 100) : 0;
  const inss = salario * (config.inss_pct / 100);
  const fgts = salario * (config.fgts_pct / 100);

  const vt = (Number(beneficio?.vale_transporte) || 0) * diasEfetivos;
  const va = (Number(beneficio?.vale_alimentacao) || 0) * diasEfetivos;
  const descVt = vt * (config.vt_desc_pct / 100);
  const descVa = va * (config.va_desc_pct / 100);

  const premio = faltas === 0 ? Number(config.premio_assiduidade_valor) || 0 : 0;
  const salFamilia = salario <= 1980.38 ? qtdFilhosElegiveis * 67.54 : 0;

  const bonus = ajustesFunc.filter(a => a.tipo === 'bonus').reduce((s, a) => s + Number(a.valor), 0);
  const adiantamento = ajustesFunc.filter(a => a.tipo === 'adiantamento').reduce((s, a) => s + Number(a.valor), 0);
  const desconto = ajustesFunc.filter(a => a.tipo === 'desconto').reduce((s, a) => s + Number(a.valor), 0);

  const totalBruto = salario + valorExtra + bonus + adicionalNoturno + premio + salFamilia + comissao;
  const totalLiquido = totalBruto - descVt - descVa - descontoFalta - adiantamento - desconto;

  return { diasUteis, diasEfetivos, faltas, horasExtras, valorExtra, adicionalNoturno, inss, fgts, vt, va, descVt, descVa, premio, salFamilia, comissao, bonus, adiantamento, desconto, totalBruto, totalLiquido };
}

export default function FolhaPagamento() {
  const hoje = new Date();
  const [mes, setMes] = useState(String(hoje.getMonth() + 1));
  const [ano, setAno] = useState(String(hoje.getFullYear()));
  const [dados, setDados] = useState({ funcionarios: [], beneficios: [], ponto: [], ajustes: [], filhos: [], comissoes: [] });
  const [config, setConfig] = useState(null);
  const [funcionarioAjuste, setFuncionarioAjuste] = useState(null);
  const [novoAjuste, setNovoAjuste] = useState({ tipo: 'bonus', valor: '', descricao: '' });
  const [fechamento, setFechamento] = useState(null);
  const [modalFechar, setModalFechar] = useState(false);
  const [dataVencimentoFolha, setDataVencimentoFolha] = useState('');
  const [fechando, setFechando] = useState(false);
  const [erroFechamento, setErroFechamento] = useState('');

  const carregar = () => {
    folhaPagamentoApi.obterDados(mes, ano).then(setDados).catch(() => {});
    folhaPagamentoApi.obterConfig().then(setConfig).catch(() => {});
    folhaPagamentoApi.obterFechamento(mes, ano).then(setFechamento).catch(() => setFechamento(null));
  };

  useEffect(() => { carregar(); }, [mes, ano]); // eslint-disable-line

  const linhas = useMemo(() => {
    if (!config) return [];
    return dados.funcionarios.map(f => {
      const beneficio = dados.beneficios.find(b => b.funcionario_id === f.id);
      const pontoMes = dados.ponto.find(p => p.funcionario_id === f.id);
      const ajustesFunc = dados.ajustes.filter(a => a.funcionario_id === f.id);
      const qtdFilhos = dados.filhos.filter(ff => ff.funcionario_id === f.id && elegivelSalarioFamilia(ff.data_nascimento)).length;
      const comissao = Number(dados.comissoes.find(c => c.funcionario_id === f.id)?.valor) || 0;
      const calc = calcularFuncionario(f, beneficio, pontoMes, ajustesFunc, config, mes, ano, qtdFilhos, comissao);
      return { funcionario: f, ajustesFunc, ...calc };
    });
  }, [dados, config, mes, ano]);

  const totais = linhas.reduce((acc, l) => ({
    bruto: acc.bruto + l.totalBruto,
    vtva: acc.vtva + l.vt + l.va,
    encargos: acc.encargos + l.inss + l.fgts,
    comissoes: acc.comissoes + l.comissao,
    liquido: acc.liquido + l.totalLiquido
  }), { bruto: 0, vtva: 0, encargos: 0, comissoes: 0, liquido: 0 });

  const salvarAjuste = async () => {
    if (!novoAjuste.valor) return;
    await ajustesFinanceirosApi.criar({
      funcionario_id: funcionarioAjuste.funcionario.id, mes, ano,
      tipo: novoAjuste.tipo, valor: novoAjuste.valor, descricao: novoAjuste.descricao
    });
    setNovoAjuste({ tipo: 'bonus', valor: '', descricao: '' });
    carregar();
  };

  const excluirAjuste = async (id) => {
    await ajustesFinanceirosApi.excluir(id);
    carregar();
  };

  const abrirFecharFolha = () => {
    const ultimoDia = new Date(Number(ano), Number(mes), 0).getDate();
    setDataVencimentoFolha(`${ano}-${String(mes).padStart(2, '0')}-${String(ultimoDia).padStart(2, '0')}`);
    setErroFechamento('');
    setModalFechar(true);
  };

  const confirmarFecharFolha = async () => {
    setFechando(true);
    setErroFechamento('');
    try {
      await folhaPagamentoApi.fechar({
        mes, ano, quantidade_funcionarios: linhas.length,
        total_bruto: totais.bruto, total_liquido: totais.liquido, data_vencimento: dataVencimentoFolha
      });
      setModalFechar(false);
      carregar();
    } catch (err) {
      setErroFechamento(err.message);
    } finally {
      setFechando(false);
    }
  };

  return (
    <div>
      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <Select options={MESES} value={mes} onChange={e => setMes(e.target.value)} style={{ width: 160 }} />
            <Select options={ANOS} value={ano} onChange={e => setAno(e.target.value)} style={{ width: 110 }} />
          </div>
          {fechamento ? (
            <Badge tom="sucesso">
              Folha fechada em {new Date(fechamento.criado_em).toLocaleDateString('pt-BR')} — lançada no Contas a Pagar
            </Badge>
          ) : (
            <Btn onClick={abrirFecharFolha} disabled={linhas.length === 0}>Fechar Folha e Lançar no Financeiro</Btn>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
          <div style={{ background: COLORS.bg, borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 11, color: COLORS.textLight, textTransform: 'uppercase', fontWeight: 700 }}>Total Bruto</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{formatarReais(totais.bruto)}</div>
          </div>
          <div style={{ background: COLORS.bg, borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 11, color: COLORS.textLight, textTransform: 'uppercase', fontWeight: 700 }}>Comissões</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{formatarReais(totais.comissoes)}</div>
          </div>
          <div style={{ background: COLORS.bg, borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 11, color: COLORS.textLight, textTransform: 'uppercase', fontWeight: 700 }}>VT + VA</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{formatarReais(totais.vtva)}</div>
          </div>
          <div style={{ background: COLORS.bg, borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 11, color: COLORS.textLight, textTransform: 'uppercase', fontWeight: 700 }}>INSS + FGTS</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{formatarReais(totais.encargos)}</div>
          </div>
          <div style={{ background: COLORS.primaryBg, borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 11, color: COLORS.primary, textTransform: 'uppercase', fontWeight: 700 }}>Total Líquido</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.primary }}>{formatarReais(totais.liquido)}</div>
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, whiteSpace: 'nowrap' }}>
          <thead>
            <tr style={{ textAlign: 'left', background: COLORS.bg }}>
              <th style={{ padding: 6 }}>Nome</th>
              <th style={{ padding: 6 }}>Cargo</th>
              <th style={{ padding: 6 }}>Salário</th>
              <th style={{ padding: 6 }}>Comissão</th>
              <th style={{ padding: 6 }}>Prêmio</th>
              <th style={{ padding: 6 }}>Sal. Família</th>
              <th style={{ padding: 6 }}>Ad. Noturno</th>
              <th style={{ padding: 6 }}>H. Extra</th>
              <th style={{ padding: 6 }}>Faltas</th>
              <th style={{ padding: 6 }}>Dias Efetivos</th>
              <th style={{ padding: 6 }}>VT</th>
              <th style={{ padding: 6 }}>VA</th>
              <th style={{ padding: 6 }}>INSS</th>
              <th style={{ padding: 6 }}>Ajustes</th>
              <th style={{ padding: 6 }}>Líquido</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map(l => (
              <tr key={l.funcionario.id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: 6, fontWeight: 600 }}>{l.funcionario.nome}</td>
                <td style={{ padding: 6 }}>{l.funcionario.cargo || '-'}</td>
                <td style={{ padding: 6 }}>{formatarReais(l.funcionario.salario)}</td>
                <td style={{ padding: 6 }}>{formatarReais(l.comissao)}</td>
                <td style={{ padding: 6 }}>{formatarReais(l.premio)}</td>
                <td style={{ padding: 6 }}>{formatarReais(l.salFamilia)}</td>
                <td style={{ padding: 6 }}>{formatarReais(l.adicionalNoturno)}</td>
                <td style={{ padding: 6 }}>{l.horasExtras}h ({formatarReais(l.valorExtra)})</td>
                <td style={{ padding: 6 }}>{l.faltas}</td>
                <td style={{ padding: 6 }}>{l.diasEfetivos}/{l.diasUteis}</td>
                <td style={{ padding: 6 }}>{formatarReais(l.vt)}</td>
                <td style={{ padding: 6 }}>{formatarReais(l.va)}</td>
                <td style={{ padding: 6 }}>{formatarReais(l.inss)}</td>
                <td style={{ padding: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button onClick={() => setFuncionarioAjuste(l)} style={{ background: 'none', border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: '3px 8px', fontSize: 10, fontWeight: 600, color: COLORS.primary, cursor: 'pointer' }}>
                      {l.ajustesFunc.length > 0 ? `${l.ajustesFunc.length} lanç.` : 'Ajustar'}
                    </button>
                    {l.ajustesFunc.some(a => a.origem === 'vale_mercadoria') && (
                      <span title="Tem Vale Mercadoria descontado automaticamente este mês" style={{ fontSize: 9, fontWeight: 700, color: COLORS.aviso || '#b45309', background: '#fef3c7', borderRadius: 5, padding: '2px 5px' }}>
                        Vale Merc.
                      </span>
                    )}
                  </div>
                </td>
                <td style={{ padding: 6, fontWeight: 700, color: COLORS.primary }}>{formatarReais(l.totalLiquido)}</td>
              </tr>
            ))}
            {linhas.length === 0 && (
              <tr><td colSpan={15} style={{ padding: 20, textAlign: 'center', color: COLORS.textMid }}>Nenhum funcionário ativo neste período.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {funcionarioAjuste && (
        <Modal titulo={`Ajustes — ${funcionarioAjuste.funcionario.nome}`} onFechar={() => setFuncionarioAjuste(null)} largura={480}
          rodape={<Btn variante="ghost" onClick={() => setFuncionarioAjuste(null)}>Fechar</Btn>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {funcionarioAjuste.ajustesFunc.map(a => (
              <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${COLORS.border}` }}>
                <div>
                  <strong style={{ textTransform: 'capitalize' }}>{a.tipo}</strong> — {formatarReais(a.valor)}
                  {a.origem === 'vale_mercadoria' && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#b45309', background: '#fef3c7', borderRadius: 5, padding: '2px 6px' }}>Vale Mercadoria</span>}
                  {a.descricao && <div style={{ fontSize: 12, color: COLORS.textMid }}>{a.descricao}</div>}
                </div>
                {a.origem === 'vale_mercadoria'
                  ? <span style={{ fontSize: 11, color: COLORS.textLight }}>via proposta</span>
                  : <button onClick={() => excluirAjuste(a.id)} style={{ background: 'none', border: 'none', color: COLORS.danger, cursor: 'pointer', fontSize: 12 }}>Excluir</button>}
              </div>
            ))}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
              <Select label="Tipo" options={[{ value: 'bonus', label: 'Bônus' }, { value: 'adiantamento', label: 'Adiantamento' }, { value: 'desconto', label: 'Desconto' }]}
                value={novoAjuste.tipo} onChange={e => setNovoAjuste(n => ({ ...n, tipo: e.target.value }))} />
              <Input label="Valor R$" type="number" step="0.01" value={novoAjuste.valor} onChange={e => setNovoAjuste(n => ({ ...n, valor: e.target.value }))} />
            </div>
            <Input label="Descrição" value={novoAjuste.descricao} onChange={e => setNovoAjuste(n => ({ ...n, descricao: e.target.value }))} />
            <Btn onClick={salvarAjuste}>+ Adicionar Lançamento</Btn>
          </div>
        </Modal>
      )}

      {modalFechar && (
        <Modal titulo="Fechar Folha de Pagamento" onFechar={() => setModalFechar(false)} largura={460}
          rodape={<>
            <Btn variante="ghost" onClick={() => setModalFechar(false)} disabled={fechando}>Cancelar</Btn>
            <Btn onClick={confirmarFecharFolha} disabled={fechando}>{fechando ? 'Fechando...' : 'Confirmar Fechamento'}</Btn>
          </>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ fontSize: 14, color: COLORS.text, margin: 0, lineHeight: 1.6 }}>
              Isso vai lançar automaticamente <strong>{formatarReais(totais.liquido)}</strong> ({linhas.length} funcionário{linhas.length === 1 ? '' : 's'})
              no Contas a Pagar, referente à folha de {MESES.find(m => m.value === String(mes))?.label}/{ano}. Depois de fechada, a folha desse mês não pode ser fechada de novo.
            </p>
            <Input label="Data de Vencimento do Pagamento" type="date" value={dataVencimentoFolha} onChange={e => setDataVencimentoFolha(e.target.value)} />
            {erroFechamento && <p style={{ color: COLORS.danger, fontSize: 13, margin: 0 }}>{erroFechamento}</p>}
          </div>
        </Modal>
      )}
    </div>
  );
}
